import React from 'react';
import { Clock, DollarSign, Zap, CheckCircle2, AlertCircle, TrendingUp, Network, AlertTriangle } from 'lucide-react';
import { CHAIN_INFO, type ChainId } from '@/config/wagmi';

interface ExecutionStep {
  step_id: number;
  agent_id: string;
  agent_name: string;
  task: string;
  capability: string;
  cost_usdc: number;
  reputation_score: number;
  estimated_duration_sec: number;
  endpoint: string;
  agent_wallet_address: string;
  wallet_chain_id?: number; // Chain ID for payment
  depends_on: number[];
}

interface ExecutionPlan {
  plan_id: string;
  query: string;
  strategy: string;
  steps: ExecutionStep[];
  total_cost_usdc: number;
  total_duration_sec: number;
  confidence_score: number;
}

interface CapabilityAnalysis {
  primary_capabilities: string[];
  secondary_capabilities: string[];
  query_type: string;
  all_matches: Array<{
    capability: string;
    score: number;
  }>;
}

interface ExecutionPlanDisplayProps {
  query: string;
  queryId: string;
  capabilityAnalysis: CapabilityAnalysis;
  executionPlans: ExecutionPlan[];
  recommendedPlan: ExecutionPlan;
  paymentDetails: {
    method: string;
    deep42_fee_usdc: number;
    user_approval_required: boolean;
    note?: string;
  };
  onExecute: () => void;
  onCancel: () => void;
  isExecuting?: boolean;
  currentChainId?: number; // Current connected chain
}

export function ExecutionPlanDisplay({
  query,
  queryId,
  capabilityAnalysis,
  executionPlans,
  recommendedPlan,
  paymentDetails,
  onExecute,
  onCancel,
  isExecuting = false,
  currentChainId,
}: ExecutionPlanDisplayProps) {
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(4)} USDC`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Execution Plan Ready
            </h2>
            <p className="text-sm text-gray-600 mb-4">{query}</p>
            <p className="text-xs text-gray-500 font-mono">Query ID: {queryId}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {recommendedPlan?.strategy ? recommendedPlan.strategy.charAt(0).toUpperCase() + recommendedPlan.strategy.slice(1) : 'Balanced'}
            </div>
          </div>
        </div>
      </div>

      {/* Network Info - Informational only */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Network className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-1">
              Multi-Chain Payment Support
            </h4>
            <p className="text-sm text-blue-800">
              During execution, you'll be prompted to switch to the correct network for each agent's payment.
              This may include Base, Ethereum, Arbitrum, or other supported chains.
            </p>
          </div>
        </div>
      </div>

      {/* Capability Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Capability Analysis
        </h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Primary Capabilities</p>
            <div className="flex flex-wrap gap-2">
              {capabilityAnalysis.primary_capabilities.map((cap) => (
                <span
                  key={cap}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Secondary Capabilities</p>
            <div className="flex flex-wrap gap-2">
              {capabilityAnalysis.secondary_capabilities.map((cap) => (
                <span
                  key={cap}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Query Type:</span>{' '}
            <span className="text-gray-900">{capabilityAnalysis.query_type}</span>
          </p>
        </div>
      </div>

      {/* Execution Steps */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Execution Steps ({recommendedPlan?.steps?.length || 0})
        </h3>
        <div className="space-y-4">
          {(recommendedPlan?.steps || []).map((step, index) => (
            <div
              key={step.step_id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{step.agent_name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{step.task}</p>
                    <p className="text-xs text-gray-500 mt-1 font-mono">
                      Agent: {step.agent_id}
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                  {step.capability}
                </span>
              </div>

              <div className="flex items-center gap-6 mt-4 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{formatCost(step.cost_usdc)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">
                    ~{formatDuration(step.estimated_duration_sec)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">
                    {(step.reputation_score * 100).toFixed(0)}% reputation
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cost Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Total Cost</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCost(recommendedPlan?.total_cost_usdc || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Deep42 Fee: {formatCost(paymentDetails.deep42_fee_usdc)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Estimated Time</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatDuration(recommendedPlan?.total_duration_sec || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {recommendedPlan?.steps?.length || 0} step{(recommendedPlan?.steps?.length || 0) !== 1 ? 's' : ''}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Confidence Score</p>
            <p className="text-2xl font-bold text-gray-900">
              {((recommendedPlan?.confidence_score || 0) * 100).toFixed(0)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Quality estimate</p>
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-2">Payment Method</h4>
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-medium">{paymentDetails.method}</span> - All payments go
              directly to agents
            </p>
            {paymentDetails.note && (
              <p className="text-sm text-gray-600">{paymentDetails.note}</p>
            )}
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Approval Required:</span> Executing this plan will
              charge {formatCost(recommendedPlan?.total_cost_usdc || 0)} from your wallet via x402
              payments. This action cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        <button
          onClick={onCancel}
          disabled={isExecuting}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onExecute}
          disabled={isExecuting}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
        >
          {isExecuting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              Execute Plan
            </>
          )}
        </button>
      </div>
    </div>
  );
}
